/**
 * Shared labels + tints for project document / attachment kinds (sales order /
 * packing slip / design package / image / video / other). Used by the project
 * workspace and the crew task screen so a document looks the same wherever it
 * surfaces.
 *
 * Prefer the `docKindLabel` / `docKindTint` helpers over indexing the maps
 * directly: they read the MIME type first, so an uploaded photo reads "Image"
 * (and a clip "Video") instead of the generic "Document" fallback — even when
 * its stored kind is the catch-all "other".
 */
const IMAGE_TINT = 'bg-teal-100 text-teal-900 border-teal-200';
const VIDEO_TINT = 'bg-rose-100 text-rose-900 border-rose-200';
const NEUTRAL_TINT = 'bg-fill-soft text-ink-dim border-line';

export const DOC_KIND_LABEL: Record<string, string> = {
  sales_order: 'Sales order',
  packing_slip: 'Packing slip',
  design_package: 'Design package',
  recap: 'Recap',
  photo: 'Image',
  video: 'Video',
  other: 'Document',
};

export const DOC_KIND_TINT: Record<string, string> = {
  sales_order: 'bg-sky-100 text-sky-900 border-sky-200',
  packing_slip: 'bg-purple-100 text-purple-900 border-purple-200',
  design_package: 'bg-emerald-100 text-emerald-900 border-emerald-200',
  recap: 'bg-amber-100 text-amber-900 border-amber-200',
  photo: IMAGE_TINT,
  video: VIDEO_TINT,
  other: NEUTRAL_TINT,
};

type DocLike = { kind: string; mime_type?: string | null };

/** Chip label for a document/attachment. Images and videos are detected by MIME
 *  so they read "Image"/"Video" regardless of their stored kind; otherwise fall
 *  back to the kind label (and finally the generic "Document"). */
export function docKindLabel(d: DocLike): string {
  if (d.mime_type?.startsWith('image/')) return 'Image';
  if (d.mime_type?.startsWith('video/')) return 'Video';
  return DOC_KIND_LABEL[d.kind] ?? 'Document';
}

/** Tint matching docKindLabel. */
export function docKindTint(d: DocLike): string {
  if (d.mime_type?.startsWith('image/')) return IMAGE_TINT;
  if (d.mime_type?.startsWith('video/')) return VIDEO_TINT;
  return DOC_KIND_TINT[d.kind] ?? NEUTRAL_TINT;
}
